import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { NIGERIAN_STATES } from "@client/lib/nigerian-states"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { SchoolFormContent, type SchoolFormValues } from "./components/school-form-modal"
import { FormsPageSkeleton } from "./components/skeleton"
import { StatusTabs } from "./components/status-tabs"
import { ContentPagination } from "./content-components"

type InquiryType = "partner" | "sponsor" | "sponsorship" | "corporate-partnership" | "school-partnership" | "media" | "other"
type InquiryStatus = "new" | "contacted" | "approved" | "declined" | "closed"

type ApplicationType = "grant" | "partnership" | "volunteer" | "partner-school" | "nomination" | "nomination-ability"
type ApplicationStatus = "submitted" | "reviewing" | "approved" | "rejected"

interface Inquiry {
  id: string
  name: string
  email: string
  organization: string | null
  type: InquiryType
  message: string
  status: InquiryStatus
  createdAt: string
  updatedAt: string
}

interface Application {
  id: string
  type: ApplicationType
  applicantName: string
  applicantEmail: string
  organization: string | null
  body: string
  status: ApplicationStatus
  createdAt: string
  updatedAt: string
}

interface AppNote {
  id: string
  applicationId: string
  author: string
  body: string
  createdAt: string
}

const INQUIRY_STATUS_COLORS: Record<InquiryStatus, { bg: string; text: string }> = {
  new: { bg: "bg-blue-100", text: "text-blue-700" },
  contacted: { bg: "bg-gold-100", text: "text-gold-700" },
  approved: { bg: "bg-green-100", text: "text-green-700" },
  declined: { bg: "bg-red-100", text: "text-red-700" },
  closed: { bg: "bg-slate-100", text: "text-slate-700" },
}

const APPLICATION_STATUS_COLORS: Record<ApplicationStatus, { bg: string; text: string }> = {
  submitted: { bg: "bg-blue-100", text: "text-blue-700" },
  reviewing: { bg: "bg-gold-100", text: "text-gold-700" },
  approved: { bg: "bg-green-100", text: "text-green-700" },
  rejected: { bg: "bg-red-100", text: "text-red-700" },
}

const INQUIRY_TYPE_LABELS: Record<InquiryType, string> = {
  partner: "Partner",
  sponsor: "Sponsor",
  sponsorship: "Sponsorship",
  "corporate-partnership": "Corporate partnership",
  "school-partnership": "School partnership",
  media: "Media",
  other: "Other",
}

const APPLICATION_TYPE_LABELS: Record<ApplicationType, string> = {
  grant: "Grant",
  partnership: "Partnership",
  volunteer: "Volunteer",
  "partner-school": "Partner school",
  nomination: "Scholarship nomination",
  "nomination-ability": "Ability Program nomination",
}

const SUBMISSION_TYPE_OPTIONS = [
  "Sponsorship",
  "Partner",
  "Corporate partnership",
  "School partnership",
  "Media",
  "Other",
  "Grant",
  "Partnership",
  "Volunteer",
  "Partner school",
  "Scholarship nomination",
  "Ability Program nomination",
]

const SUBMISSION_STATUS_COLORS: Record<SubmissionStatus, { bg: string; text: string }> = {
  submitted: { bg: "bg-blue-100", text: "text-blue-700" },
  reviewing: { bg: "bg-gold-100", text: "text-gold-700" },
  approved: { bg: "bg-green-100", text: "text-green-700" },
  rejected: { bg: "bg-red-100", text: "text-red-700" },
  closed: { bg: "bg-slate-100", text: "text-slate-700" },
}

function inquiryTypeLabel(type: InquiryType): string {
  return type === "sponsor" || type === "sponsorship" ? "Sponsorship" : INQUIRY_TYPE_LABELS[type]
}

function normalizeInquiryStatus(status: InquiryStatus): SubmissionStatus {
  if (status === "new") return "submitted"
  if (status === "contacted") return "reviewing"
  if (status === "declined") return "rejected"
  return status
}

function formatSubmissionStatus(status: SubmissionStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function normalizeSubmissionStatusFilter(status: string): SubmissionStatus {
  return status.toLowerCase() as SubmissionStatus
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  )
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M12 6h.01" />
      <path d="M12 10h.01" />
      <path d="M12 14h.01" />
      <path d="M16 10h.01" />
      <path d="M16 14h.01" />
      <path d="M8 10h.01" />
      <path d="M8 14h.01" />
    </svg>
  )
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function applicationField(body: string, label: string): string {
  const prefix = `${label}: `
  const line = body.split("\n").find((entry) => entry.startsWith(prefix))
  return line?.slice(prefix.length).trim() ?? ""
}

function schoolApprovalForm(application: Application): SchoolFormValues {
  const location = applicationField(application.body, "Location")
  const locationParts = location.split(",").map((part) => part.trim()).filter(Boolean)
  const contact = applicationField(application.body, "Contact")
  const contactMatch = contact.match(/^(.*?)\s*\((.*?)\)$/)
  const state = locationParts.length > 1
    ? locationParts[1]
    : NIGERIAN_STATES.includes(location as typeof NIGERIAN_STATES[number])
      ? location
      : ""

  return {
    name: application.organization || applicationField(application.body, "School"),
    address: "",
    city: locationParts.length > 1 ? locationParts[0] ?? "" : state ? "" : location,
    state: state ?? "",
    lga: "",
    enrollment: applicationField(application.body, "Current student enrollment"),
    type: "primary",
    contactName: contactMatch?.[1]?.trim() || application.applicantName,
    contactTitle: contactMatch?.[2]?.trim() || "",
    contactPhone: "",
    contactEmail: application.applicantEmail,
    notes: application.body,
  }
}

type TabType = "submissions" | "builder"
const PAGE_SIZE = 10

type SubmissionStatus = "submitted" | "reviewing" | "approved" | "rejected" | "closed"
type ApplicationModalView = "detail" | "confirm-status" | "approve-school"

interface Submission {
  id: string
  kind: "inquiry" | "application"
  name: string
  email: string
  organization: string | null
  type: string
  status: SubmissionStatus
  createdAt: string
  inquiry?: Inquiry
  application?: Application
}

type FormStatus = "draft" | "published" | "archived"

interface FormItem {
  id: string
  code: string
  name: string
  status: FormStatus
  fieldCount: number
  submissionCount: number
  publishedAt: string | null
  updatedAt: string
}

const FORM_STATUS_COLORS: Record<FormStatus, { bg: string; text: string }> = {
  draft: { bg: "bg-gray-100", text: "text-gray-700" },
  published: { bg: "bg-green-100", text: "text-green-700" },
  archived: { bg: "bg-red-100", text: "text-red-700" },
}

export function FormsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const canView = useHasPermission("forms")
  const canMutate = useHasPermission("forms_mutate")
  const getTabFromSearch = (value: string | null): TabType => value === "builder" ? "builder" : "submissions"
  const [activeTab, setActiveTab] = useState<TabType>(() => getTabFromSearch(searchParams.get("tab")))
  const [inquiries, setInquiries] = useState<Inquiry[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [notes, setNotes] = useState<AppNote[]>([])
  const [customForms, setCustomForms] = useState<FormItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [search, setSearch] = useState("")
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState("")
  const [submissionTypeFilter, setSubmissionTypeFilter] = useState("")
  const [submissionsPage, setSubmissionsPage] = useState(1)
  const [formsPage, setFormsPage] = useState(1)

  // Selection
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null)
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null)

  useEffect(() => {
    setActiveTab(getTabFromSearch(searchParams.get("tab")))
  }, [searchParams])

  const clearSelectionParam = (param: "inquiryId" | "applicationId") => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete(param)
    setSearchParams(nextParams, { replace: true })
  }

  const selectTab = (tab: TabType) => {
    setActiveTab(tab)
    setSearchParams({ tab })
    setSearch("")
  }

  useEffect(() => {
    setSubmissionsPage(1)
    setFormsPage(1)
  }, [activeTab, search, submissionStatusFilter, submissionTypeFilter])

  const openInquiry = (inquiry: Inquiry) => {
    clearSelectionParam("inquiryId")
    setSelectedInquiry(inquiry)
  }

  const openApplication = (application: Application) => {
    clearSelectionParam("applicationId")
    setSelectedApplication(application)
  }

  const fetchInquiries = async () => {
    try {
      const res = await client.api.admin.data.inquiries.$get({ query: {} })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setInquiries(body.data.items as Inquiry[])
      }
    } catch {
      setError("Failed to load inquiries")
    }
  }

  const fetchApplications = async () => {
    try {
      const res = await client.api.admin.data.applications.$get()
      const body = await res.json()
      if (res.ok && "data" in body) {
        setApplications(body.data as Application[])
      }
    } catch {
      setError("Failed to load applications")
    }
  }

  const fetchNotes = async () => {
    try {
      const res = await client.api.admin.data.notes.$get()
      const body = await res.json()
      if (res.ok && "data" in body) {
        setNotes(body.data as AppNote[])
      }
    } catch {
      // Silently fail
    }
  }

  const fetchCustomForms = async () => {
    try {
      const res = await client.api.admin.forms.$get({ query: {} })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setCustomForms(body.data.items as FormItem[])
      }
    } catch {
      // Silently fail
    }
  }

  useEffect(() => {
    if (canView) {
      Promise.all([fetchInquiries(), fetchApplications(), fetchNotes(), fetchCustomForms()]).finally(() => {
        setLoading(false)
      })
    } else {
      setLoading(false)
    }
  }, [canView])

  useEffect(() => {
    const inquiryId = searchParams.get("inquiryId")
    if (inquiryId) {
      const inquiry = inquiries.find((item) => item.id === inquiryId)
      if (inquiry) setSelectedInquiry(inquiry)
    }

    const applicationId = searchParams.get("applicationId")
    if (applicationId) {
      const application = applications.find((item) => item.id === applicationId)
      if (application) setSelectedApplication(application)
    }
  }, [applications, inquiries, searchParams])

  const submissions: Submission[] = [
    ...inquiries.map((inquiry) => ({
      id: inquiry.id,
      kind: "inquiry" as const,
      name: inquiry.name,
      email: inquiry.email,
      organization: inquiry.organization,
      type: inquiryTypeLabel(inquiry.type),
      status: normalizeInquiryStatus(inquiry.status),
      createdAt: inquiry.createdAt,
      inquiry,
    })),
    ...applications.map((application) => ({
      id: application.id,
      kind: "application" as const,
      name: application.applicantName,
      email: application.applicantEmail,
      organization: application.organization,
      type: APPLICATION_TYPE_LABELS[application.type],
      status: application.status,
      createdAt: application.createdAt,
      application,
    })),
  ]

  const filteredSubmissions = submissions
    .filter((submission) => {
      const query = search.toLowerCase()
      const matchesSearch = !query || [submission.name, submission.email, submission.organization ?? ""]
        .some((value) => value.toLowerCase().includes(query))
      const matchesStatus = !submissionStatusFilter || submission.status === submissionStatusFilter
      const matchesType = !submissionTypeFilter || submission.type === submissionTypeFilter
      return matchesSearch && matchesStatus && matchesType
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  const filteredForms = customForms.filter((form) =>
    !search || form.name.toLowerCase().includes(search.toLowerCase()) || form.code.toLowerCase().includes(search.toLowerCase()),
  )
  const submissionPageRows = filteredSubmissions.slice((submissionsPage - 1) * PAGE_SIZE, submissionsPage * PAGE_SIZE)
  const formPageRows = filteredForms.slice((formsPage - 1) * PAGE_SIZE, formsPage * PAGE_SIZE)

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(filteredSubmissions.length / PAGE_SIZE))
    if (submissionsPage > pageCount) setSubmissionsPage(pageCount)
  }, [filteredSubmissions.length, submissionsPage])

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(filteredForms.length / PAGE_SIZE))
    if (formsPage > pageCount) setFormsPage(pageCount)
  }, [filteredForms.length, formsPage])

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Forms</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view forms.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <FormsPageSkeleton />
  }

  const tabs = [
    { key: "submissions" as const, label: "Submissions", count: submissions.length },
    { key: "builder" as const, label: "Form Builder", count: customForms.length },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Forms</h1>
        <p className="mt-1 text-sm text-ink/60">
          All website submissions, from first contact to formal review.
        </p>
      </div>

      <StatusTabs tabs={tabs} activeKey={activeTab} onChange={selectTab} ariaLabel="Forms sections" />

      {/* Search & Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or organization..."
            className="w-full rounded-lg border border-gold-500 bg-cream-50 py-2.5 pl-10 pr-4 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
        {activeTab === "submissions" && (
          <>
            <div className="w-40">
              <Select
                value={submissionStatusFilter ? formatSubmissionStatus(submissionStatusFilter as SubmissionStatus) : "All statuses"}
                onChange={(value) => setSubmissionStatusFilter(value === "All statuses" ? "" : normalizeSubmissionStatusFilter(value))}
                options={["All statuses", "Submitted", "Reviewing", "Approved", "Rejected", "Closed"]}
              />
            </div>
            <div className="w-48">
              <Select
                value={submissionTypeFilter || "All types"}
                onChange={(value) => setSubmissionTypeFilter(value === "All types" ? "" : value)}
                options={["All types", ...SUBMISSION_TYPE_OPTIONS]}
              />
            </div>
          </>
        )}
        {activeTab === "builder" && canMutate && (
          <button
            onClick={() => navigate("/admin/forms/builder/new")}
            className="ml-auto rounded-lg bg-forest-950 px-4 py-2.5 text-sm font-medium text-white hover:bg-forest-900"
          >
            Create Form
          </button>
        )}
      </div>

      {/* Content */}
      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : activeTab === "submissions" ? (
        <>
          <SubmissionsTable
            submissions={submissionPageRows}
            onSelect={(submission) => {
              if (submission.kind === "inquiry" && submission.inquiry) openInquiry(submission.inquiry)
              if (submission.kind === "application" && submission.application) openApplication(submission.application)
            }}
          />
          <ContentPagination
            page={submissionsPage}
            pageSize={PAGE_SIZE}
            total={filteredSubmissions.length}
            onPageChange={setSubmissionsPage}
            disabled={loading}
          />
        </>
      ) : (
        <>
          <CustomFormsTable
            forms={formPageRows}
            onSelect={(form) => navigate(`/admin/forms/builder/${form.id}`)}
          />
          <ContentPagination
            page={formsPage}
            pageSize={PAGE_SIZE}
            total={filteredForms.length}
            onPageChange={setFormsPage}
            disabled={loading}
          />
        </>
      )}

      {/* Inquiry Detail Modal */}
      <InquiryDetailModal
        inquiry={selectedInquiry}
        onClose={() => {
          setSelectedInquiry(null)
          clearSelectionParam("inquiryId")
        }}
        canMutate={canMutate}
        onStatusChange={() => void fetchInquiries()}
      />

      {/* Application Detail Modal */}
      <ApplicationDetailModal
        application={selectedApplication}
        notes={notes.filter((n) => n.applicationId === selectedApplication?.id)}
        onClose={() => {
          setSelectedApplication(null)
          clearSelectionParam("applicationId")
        }}
        canMutate={canMutate}
        onStatusChange={() => void fetchApplications()}
        onNoteAdded={() => void fetchNotes()}
      />
    </div>
  )
}

function SubmissionsTable({
  submissions,
  onSelect,
}: {
  submissions: Submission[]
  onSelect: (submission: Submission) => void
}) {
  if (submissions.length === 0) {
    return (
      <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
        <MailIcon className="mx-auto h-12 w-12 text-ink/30" />
        <h3 className="mt-4 text-lg font-semibold text-ink">No submissions</h3>
        <p className="mt-2 text-sm text-ink/60">No submissions match your filters.</p>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gold-500/50 bg-cream-50">
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Submitted by</th>
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Organization</th>
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Type</th>
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Status</th>
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Received</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {submissions.map((submission) => {
              const colors = SUBMISSION_STATUS_COLORS[submission.status]
              return (
                <tr
                  key={`${submission.kind}:${submission.id}`}
                  onClick={() => onSelect(submission)}
                  className="cursor-pointer hover:bg-cream-50"
                >
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium text-ink">{submission.name}</p>
                      <p className="text-xs text-ink/50">{submission.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-ink/70">{submission.organization || "—"}</td>
                  <td className="px-4 py-4">
                    <span className="rounded-full bg-cream-100 px-2.5 py-1 text-xs font-medium text-ink/70">
                      {submission.type}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${colors.bg} ${colors.text}`}>
                      {formatSubmissionStatus(submission.status)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-ink/50">{formatDate(submission.createdAt)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function CustomFormsTable({
  forms,
  onSelect,
}: {
  forms: FormItem[]
  onSelect: (form: FormItem) => void
}) {
  if (forms.length === 0) {
    return (
      <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
        <svg className="mx-auto h-12 w-12 text-ink/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        <h3 className="mt-4 text-lg font-semibold text-ink">No forms</h3>
        <p className="mt-2 text-sm text-ink/60">
          Create your first custom form to collect data.
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gold-500/50 bg-cream-50">
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Form</th>
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Code</th>
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Fields</th>
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Submissions</th>
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Status</th>
              <th className="px-4 py-4 text-left text-sm font-bold text-ink">Last Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {forms.map((form) => (
              <tr
                key={form.id}
                onClick={() => onSelect(form)}
                className="cursor-pointer hover:bg-cream-50"
              >
                <td className="px-4 py-4">
                  <p className="font-medium text-ink">{form.name}</p>
                </td>
                <td className="px-4 py-4 text-sm text-ink/70">
                  {form.code}
                </td>
                <td className="px-4 py-4 text-sm text-ink/70">
                  {form.fieldCount}
                </td>
                <td className="px-4 py-4 text-sm text-ink/70">
                  {form.submissionCount}
                </td>
                <td className="px-4 py-4">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
                      FORM_STATUS_COLORS[form.status].bg
                    } ${FORM_STATUS_COLORS[form.status].text}`}
                  >
                    {form.status}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-ink/50">
                  {formatDate(form.updatedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function InquiryDetailModal({
  inquiry,
  onClose,
  canMutate,
  onStatusChange,
}: {
  inquiry: Inquiry | null
  onClose: () => void
  canMutate: boolean
  onStatusChange: () => void
}) {
  const [updating, setUpdating] = useState(false)

  if (!inquiry) return null

  const handleStatusChange = async (newStatus: InquiryStatus) => {
    setUpdating(true)
    try {
      const res = await client.api.admin.data.inquiries[":id"].$patch({
        param: { id: inquiry.id },
        json: { status: newStatus },
      })
      if (res.ok) {
        toast.success(`Status updated to ${newStatus}`)
        onStatusChange()
        onClose()
      } else {
        const body = await res.json()
        toast.error(getErrorMessage(body))
      }
    } catch {
      toast.error("Failed to update status")
    } finally {
      setUpdating(false)
    }
  }

  return (
    <Modal
      isOpen={!!inquiry}
      onClose={onClose}
      title={inquiry.name}
      subtitle={`${INQUIRY_TYPE_LABELS[inquiry.type]} inquiry`}
      maxWidth="lg"
    >
      <div className="space-y-6">
        {/* Status Badge */}
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold capitalize ${
              INQUIRY_STATUS_COLORS[inquiry.status].bg
            } ${INQUIRY_STATUS_COLORS[inquiry.status].text}`}
          >
            {inquiry.status}
          </span>
          <span className="text-sm text-ink/50">
            Received {formatDateTime(inquiry.createdAt)}
          </span>
        </div>

        {/* Contact Info */}
        <div className="rounded-lg border border-gold-500/50 bg-cream-50 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-3">
              <MailIcon className="h-5 w-5 text-ink/50" />
              <div>
                <p className="text-xs text-ink/50">Email</p>
                <a href={`mailto:${inquiry.email}`} className="text-sm font-medium text-forest-700 hover:underline">
                  {inquiry.email}
                </a>
              </div>
            </div>
            {inquiry.organization && (
              <div className="flex items-center gap-3">
                <BuildingIcon className="h-5 w-5 text-ink/50" />
                <div>
                  <p className="text-xs text-ink/50">Organization</p>
                  <p className="text-sm font-medium text-ink">{inquiry.organization}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Message */}
        <div>
          <h3 className="mb-2 text-sm font-medium text-ink">Message</h3>
          <div className="rounded-lg border border-gold-500/50 bg-white p-4">
            <p className="whitespace-pre-wrap text-sm text-ink/80">{inquiry.message}</p>
          </div>
        </div>

        {/* Status Actions */}
        {canMutate && (
          <div>
            <h3 className="mb-2 text-sm font-medium text-ink">Update Status</h3>
            <div className="flex flex-wrap gap-2">
              {(["new", "contacted", "approved", "declined", "closed"] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  disabled={updating || inquiry.status === status}
                  className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors disabled:opacity-50 ${
                    inquiry.status === status
                      ? `${INQUIRY_STATUS_COLORS[status].bg} ${INQUIRY_STATUS_COLORS[status].text}`
                      : "border border-ink/20 text-ink hover:bg-cream-100"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        )}

        <ModalActions>
          <ModalButton onClick={onClose}>Close</ModalButton>
        </ModalActions>
      </div>
    </Modal>
  )
}

function ApplicationDetailModal({
  application,
  notes,
  onClose,
  canMutate,
  onStatusChange,
  onNoteAdded,
}: {
  application: Application | null
  notes: AppNote[]
  onClose: () => void
  canMutate: boolean
  onStatusChange: () => void
  onNoteAdded: () => void
}) {
  const [updating, setUpdating] = useState(false)
  const [newNote, setNewNote] = useState("")
  const [addingNote, setAddingNote] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<ApplicationStatus | null>(null)
  const [schoolForm, setSchoolForm] = useState<SchoolFormValues | null>(null)
  const [modalView, setModalView] = useState<ApplicationModalView>("detail")

  useEffect(() => {
    if (!application) {
      setPendingStatus(null)
      setSchoolForm(null)
      setModalView("detail")
      return
    }
    setSchoolForm(schoolApprovalForm(application))
    setModalView("detail")
  }, [application?.id])

  if (!application) return null

  const requestStatusChange = (newStatus: ApplicationStatus) => {
    if (newStatus === application.status) return
    setPendingStatus(newStatus)
    if (newStatus === "approved" && application.type === "partner-school") {
      setSchoolForm(schoolApprovalForm(application))
      setModalView("approve-school")
    } else {
      setModalView("confirm-status")
    }
  }

  const handleStatusChange = async (school?: SchoolFormValues) => {
    if (!pendingStatus) return

    setUpdating(true)
    try {
      const res = await client.api.admin.data.applications[":id"].$patch({
        param: { id: application.id },
        json: {
          status: pendingStatus,
          ...(pendingStatus === "approved" && application.type === "partner-school" && school
            ? {
                school: {
                  name: school.name.trim(),
                  address: school.address.trim() || null,
                  city: school.city.trim(),
                  state: school.state.trim(),
                  lga: school.lga.trim() || null,
                  enrollment: Number.parseInt(school.enrollment, 10) || 0,
                  type: school.type,
                  contactName: school.contactName.trim(),
                  contactTitle: school.contactTitle.trim() || null,
                  contactPhone: school.contactPhone.trim() || null,
                  contactEmail: school.contactEmail.trim() || null,
                  notes: school.notes.trim() || null,
                },
              }
            : {}),
        },
      })
      if (res.ok) {
        toast.success(pendingStatus === "approved" && application.type === "partner-school" ? "Approved and added to schools" : `Status updated to ${pendingStatus}`)
        setPendingStatus(null)
        onStatusChange()
        onClose()
      } else {
        const body = await res.json()
        toast.error(getErrorMessage(body))
      }
    } catch {
      toast.error("Failed to update status")
    } finally {
      setUpdating(false)
    }
  }

  const handleAddNote = async () => {
    if (!newNote.trim()) return
    setAddingNote(true)
    try {
      const res = await client.api.admin.data.notes.$post({
        json: {
          id: crypto.randomUUID(),
          applicationId: application.id,
          body: newNote.trim(),
        },
      })
      if (res.ok) {
        toast.success("Note added")
        setNewNote("")
        onNoteAdded()
      } else {
        const body = await res.json()
        toast.error(getErrorMessage(body))
      }
    } catch {
      toast.error("Failed to add note")
    } finally {
      setAddingNote(false)
    }
  }

  return (
    <Modal
      isOpen={!!application}
      onClose={onClose}
      title={modalView === "detail" ? application.applicantName : modalView === "approve-school" ? "Approve & Add to Schools" : "Change Application Status"}
      subtitle={modalView === "detail" ? `${APPLICATION_TYPE_LABELS[application.type]} application` : modalView === "approve-school" ? "Review the school details before creating the school record." : "Confirm the new application status."}
      maxWidth="xl"
    >
      {modalView === "detail" ? (
        <div className="space-y-6">
        {/* Status Badge */}
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold capitalize ${
              APPLICATION_STATUS_COLORS[application.status].bg
            } ${APPLICATION_STATUS_COLORS[application.status].text}`}
          >
            {application.status}
          </span>
          <span className="text-sm text-ink/50">
            Received {formatDateTime(application.createdAt)}
          </span>
        </div>

        {/* Contact Info */}
        <div className="rounded-lg border border-gold-500/50 bg-cream-50 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-3">
              <MailIcon className="h-5 w-5 text-ink/50" />
              <div>
                <p className="text-xs text-ink/50">Email</p>
                <a href={`mailto:${application.applicantEmail}`} className="text-sm font-medium text-forest-700 hover:underline">
                  {application.applicantEmail}
                </a>
              </div>
            </div>
            {application.organization && (
              <div className="flex items-center gap-3">
                <BuildingIcon className="h-5 w-5 text-ink/50" />
                <div>
                  <p className="text-xs text-ink/50">Organization</p>
                  <p className="text-sm font-medium text-ink">{application.organization}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Application Body */}
        <div>
          <h3 className="mb-2 text-sm font-medium text-ink">Application</h3>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-gold-500/50 bg-white p-4">
            <p className="whitespace-pre-wrap text-sm text-ink/80">{application.body}</p>
          </div>
        </div>

        {/* Notes */}
        <div>
          <h3 className="mb-2 text-sm font-medium text-ink">Notes ({notes.length})</h3>
          <div className="space-y-3">
            {notes.length === 0 ? (
              <p className="text-sm text-ink/50">No notes yet.</p>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="rounded-lg border border-gold-500/50 bg-white p-3">
                  <div className="flex items-center gap-2 text-xs text-ink/50">
                    <UserIcon className="h-3 w-3" />
                    <span>{note.author}</span>
                    <span>•</span>
                    <span>{formatDateTime(note.createdAt)}</span>
                  </div>
                  <p className="mt-2 text-sm text-ink/80">{note.body}</p>
                </div>
              ))
            )}

            {canMutate && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 rounded-lg border border-gold-500 bg-cream-50 px-3 py-2 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault()
                      void handleAddNote()
                    }
                  }}
                />
                <button
                  onClick={() => void handleAddNote()}
                  disabled={addingNote || !newNote.trim()}
                  className="rounded-lg bg-forest-950 px-4 py-2 text-sm font-medium text-white hover:bg-forest-900 disabled:opacity-50"
                >
                  {addingNote ? "Adding..." : "Add"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Status Actions */}
        {canMutate && (
          <div>
            <h3 className="mb-2 text-sm font-medium text-ink">Update Status</h3>
            <div className="flex flex-wrap gap-2">
              {(["submitted", "reviewing", "approved", "rejected"] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => requestStatusChange(status)}
                  disabled={updating || application.status === status}
                  className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors disabled:opacity-50 ${
                    application.status === status
                      ? `${APPLICATION_STATUS_COLORS[status].bg} ${APPLICATION_STATUS_COLORS[status].text}`
                      : "border border-ink/20 text-ink hover:bg-cream-100"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        )}

        <ModalActions>
          <ModalButton onClick={onClose}>Close</ModalButton>
        </ModalActions>
        </div>
      ) : modalView === "confirm-status" ? (
        <div>
          <p className="text-sm text-ink/70">
            Change this application from <strong>{application.status}</strong> to <strong>{pendingStatus}</strong>?
          </p>
          <ModalActions>
            <ModalButton onClick={() => setModalView("detail")}>Back</ModalButton>
            <ModalButton onClick={onClose}>Cancel</ModalButton>
            <ModalButton variant="primary" disabled={updating} onClick={() => void handleStatusChange()}>
              {updating ? "Updating..." : "Confirm change"}
            </ModalButton>
          </ModalActions>
        </div>
      ) : (
        schoolForm && (
          <SchoolFormContent
            values={schoolForm}
            onChange={(values) => setSchoolForm(values)}
            onSubmit={() => handleStatusChange(schoolForm)}
            onBack={() => setModalView("detail")}
            onCancel={onClose}
            saving={updating}
            submitLabel="Approve & Add"
          />
        )
      )}
    </Modal>
  )
}
